import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isNoindexRoute, PRIVATE_ROBOTS_DIRECTIVES } from '../utils/seoPolicy';

interface SEOProps {
    title: string;
    description: string;
    keywords?: string;
    image?: string; // Full URL preferably
    schema?: object;
    type?: 'website' | 'article' | 'product';
    noindex?: boolean;
    hreflangs?: { lang: string; url: string }[];
}

const softenSchemaClaimText = (value: string): string => value
    .replace(/boost SEO rankings/gi, 'support SEO analysis')
    .replace(/boost your SERP rankings/gi, 'support SERP performance analysis')
    .replace(/manipulate CTR/gi, 'analyze CTR')
    .replace(/CTR manipulation/gi, 'CTR analysis')
    .replace(/boost rankings/gi, 'support search visibility analysis')
    .replace(/organic rankings/gi, 'organic visibility analysis')
    .replace(/gets millions of views/gi, 'supports repeatable video publishing workflows')
    .replace(/get millions of views/gi, 'support repeatable video publishing workflows')
    .replace(/viral faceless TikTok videos/gi, 'faceless TikTok videos')
    .replace(/viral-ready scripts/gi, 'short-form scripts')
    .replace(/go viral/gi, 'perform well')
    .replace(/actually goes viral/gi, 'matches each client niche');

const sanitizeSchema = (value: unknown): unknown => {
    if (Array.isArray(value)) {
        return value.map(sanitizeSchema);
    }

    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>)
                .filter(([key]) => key !== 'aggregateRating')
                .map(([key, entry]) => [key, sanitizeSchema(entry)])
        );
    }

    if (typeof value === 'string') {
        return softenSchemaClaimText(value);
    }

    return value;
};

const schemaReferencesCanonical = (value: unknown, canonicalUrl: string): boolean => {
    if (typeof value === 'string') {
        return value === canonicalUrl || value.startsWith(`${canonicalUrl}#`);
    }
    if (Array.isArray(value)) {
        return value.some((entry) => schemaReferencesCanonical(entry, canonicalUrl));
    }
    if (value && typeof value === 'object') {
        return Object.values(value as Record<string, unknown>)
            .some((entry) => schemaReferencesCanonical(entry, canonicalUrl));
    }
    return false;
};

const SEO: React.FC<SEOProps> = ({
    title,
    description,
    keywords,
    image,
    schema,
    type = 'website',
    noindex = false,
    hreflangs
}) => {
    const location = useLocation();
    const shouldNoindex = noindex || isNoindexRoute(location.pathname);
    const domain = 'https://traffic-creator.com';
    const canonicalPath = location.pathname === '/'
        ? '/'
        : location.pathname.replace(/\/+$/, '');
    const canonicalUrl = `${domain}${canonicalPath}`;
    const defaultImage = `${domain}/og-landing-new.jpg`;
    const finalImage = image ? (image.startsWith('http') ? image : `${domain}${image}`) : defaultImage;
    const sanitizedSchema = schema && !shouldNoindex ? sanitizeSchema(schema) : undefined;

    // Determine og:locale from URL path prefix
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const langPrefix = pathSegments.length > 0 ? pathSegments[0] : '';
    const localeMap: Record<string, string> = {
        'de': 'de_DE', 'fr': 'fr_FR', 'es': 'es_ES', 'pt': 'pt_BR', 'it': 'it_IT',
        'ja': 'ja_JP', 'ko': 'ko_KR', 'zh': 'zh_CN', 'ru': 'ru_RU', 'tr': 'tr_TR',
        'id': 'id_ID', 'nl': 'nl_NL', 'ar': 'ar_SA', 'pl': 'pl_PL', 'th': 'th_TH',
        'vi': 'vi_VN', 'tl': 'tl_PH', 'ro': 'ro_RO', 'sv': 'sv_SE', 'he': 'he_IL',
        'bn': 'bn_BD', 'cs': 'cs_CZ', 'hi': 'hi_IN',
    };
    const ogLocale = localeMap[langPrefix] || 'en_US';
    const documentLanguage = Object.prototype.hasOwnProperty.call(localeMap, langPrefix)
        ? langPrefix
        : 'en';
    const serializedSchema = sanitizedSchema ? JSON.stringify(sanitizedSchema) : '';

    useEffect(() => {
        const head = document.head;
        const runtimeSelector = '[data-tc-seo-runtime="true"]';
        const routeSeoSelector = [
            'link[rel="canonical"]',
            'link[rel="alternate"][hreflang]',
            'meta[name="description"]',
            'meta[name="keywords"]',
            'meta[name="robots"]',
            'meta[name="googlebot"]',
            'meta[name="bingbot"]',
            'meta[property^="og:"]',
            'meta[name^="twitter:"]',
        ].join(', ');

        document.documentElement.lang = documentLanguage;

        head.querySelectorAll(`${runtimeSelector}, [data-rh="true"]`).forEach((tag) => tag.remove());
        head.querySelectorAll(routeSeoSelector).forEach((tag) => tag.remove());

        const serverSchemaScripts = Array.from(
            head.querySelectorAll<HTMLScriptElement>(
                'script[type="application/ld+json"]:not([data-tc-seo-runtime="true"])'
            )
        );
        const matchingServerSchema = !shouldNoindex
            ? serverSchemaScripts.find((script) => {
                try {
                    return schemaReferencesCanonical(
                        JSON.parse(script.textContent || ''),
                        canonicalUrl,
                    );
                } catch {
                    return false;
                }
            })
            : undefined;
        serverSchemaScripts
            .filter((script) => script !== matchingServerSchema)
            .forEach((script) => script.remove());

        const appendMeta = (attribute: 'name' | 'property', key: string, content: string) => {
            const meta = document.createElement('meta');
            meta.setAttribute(attribute, key);
            meta.setAttribute('content', content);
            meta.dataset.tcSeoRuntime = 'true';
            head.appendChild(meta);
        };

        const appendLink = (rel: string, href: string, hrefLang?: string) => {
            const link = document.createElement('link');
            link.rel = rel;
            link.href = href;
            if (hrefLang) link.hreflang = hrefLang;
            link.dataset.tcSeoRuntime = 'true';
            head.appendChild(link);
        };

        document.title = title;
        appendMeta('name', 'description', description);
        if (keywords) appendMeta('name', 'keywords', keywords);

        if (shouldNoindex) {
            appendMeta('name', 'robots', PRIVATE_ROBOTS_DIRECTIVES);
            appendMeta('name', 'googlebot', PRIVATE_ROBOTS_DIRECTIVES);
            appendMeta('name', 'bingbot', PRIVATE_ROBOTS_DIRECTIVES);
        } else {
            appendLink('canonical', canonicalUrl);
            hreflangs?.forEach((entry) => appendLink('alternate', entry.url, entry.lang));
            appendMeta('name', 'robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
            appendMeta('property', 'og:type', type);
            appendMeta('property', 'og:url', canonicalUrl);
            appendMeta('property', 'og:title', title);
            appendMeta('property', 'og:description', description);
            appendMeta('property', 'og:image', finalImage);
            appendMeta('property', 'og:image:width', '1200');
            appendMeta('property', 'og:image:height', '630');
            appendMeta('property', 'og:image:alt', `${title} — Traffic Creator`);
            appendMeta('property', 'og:site_name', 'Traffic Creator');
            appendMeta('property', 'og:locale', ogLocale);
            appendMeta('name', 'twitter:card', 'summary_large_image');
            appendMeta('name', 'twitter:site', '@trafficcreator');
            appendMeta('name', 'twitter:url', canonicalUrl);
            appendMeta('name', 'twitter:title', title);
            appendMeta('name', 'twitter:description', description);
            appendMeta('name', 'twitter:image', finalImage);
            appendMeta('name', 'twitter:image:alt', `${title} — Traffic Creator`);

            if (serializedSchema && !matchingServerSchema) {
                const script = document.createElement('script');
                script.type = 'application/ld+json';
                script.textContent = serializedSchema;
                script.dataset.tcSeoRuntime = 'true';
                head.appendChild(script);
            }
        }

        return () => {
            head.querySelectorAll(runtimeSelector).forEach((tag) => tag.remove());
        };
    }, [canonicalUrl, description, documentLanguage, finalImage, hreflangs, keywords, ogLocale, serializedSchema, shouldNoindex, title, type]);

    return null;
};

export default SEO;
